import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Stack,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from "@mui/material";
import { Edit, Delete as DeleteIcon } from "@mui/icons-material";
import { useMsal } from "@azure/msal-react";
import Loading from "../components/loading";
import Breadcrumbs from "../components/breadcrumbs";
import Dialogbox from "../components/dialogs/dialogBox";
import CodeEditor from "../components/monacoEditor";
import SecretDetailsCard from "../components/lastUpdatedby";
import TimeFormatter from "../components/dialogs/timeFormater";
import { useSecrets } from "../components/hooks/useSecrets";
import { useApiHeaders } from "../components/hooks/useApiHeaders";
import { getTagValue } from "../components/utils/tagHelpers";
import { config } from "../components/config";
import useAccessToken from "../auth/accessPermission";
import useUserAccount from "../components/hooks/userAccountInfo";

const containerStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  minHeight: "100vh",
};

const boxStyles = {
  bgcolor: "background.paper",
  m: 1,
  border: 1,
  width: "60rem",
  height: "50rem",
};

const headerStyles = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderBottom: 1,
  borderColor: "grey.300",
  bgcolor: "grey.100",
  height: "2.5rem",
};

function ViewSecret() {
  const { secretName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const selectedCluster = searchParams.get("selectedCluster");
  const { instance } = useMsal();
  
  const [secret, setSecret] = useState("");
  const [secretTag, setSecretTag] = useState([]);
  const [updatedOn, setUpdatedOn] = useState([]);
  const [version, setVersion] = useState("current");
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openWarning, setOpenWarning] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [rollbackMessage, setRollbackMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const accessToken = useAccessToken([config.apiAccess]);
  const { project, userRole, nameParts } = useUserAccount(instance);
  const { loading, fetchSecret, deleteSecret, rollbackSecret } = useSecrets();
  
  const [platformName, vaultId] = config.secretStore[selectedCluster] || [];
  const headers = useApiHeaders(accessToken, userRole, project, platformName, vaultId);

  const loadSecret = useCallback(async () => {
    if (headers && userRole && accessToken) {
      try {
        const response = await fetchSecret(secretName, platformName, headers, version);
        const secretData = response.secretValue;
        const formattedSecret = secretData["appsettings.json"]
          ? JSON.stringify(secretData["appsettings.json"], null, 2)
          : JSON.stringify(secretData, null, 2);
        setSecret(formattedSecret);
        setSecretTag(response.tags);
        setUpdatedOn(response.updated_on);
      } catch (error) {
        if (error.response?.status === 404 && version === "previous") {
          setRollbackMessage("No previous version available.");
          setOpenError(true);
        } else if (error.response?.status === 403) {
          setErrorMessage("Access Denied!!");
          setOpenError(true);
        }
      }
    }
  }, [secretName, version, platformName, headers, userRole, accessToken, fetchSecret]);

  useEffect(() => {
    loadSecret();
  }, [loadSecret]);

  const handleDeleteSecret = useCallback(async () => {
    if (userRole !== config.roles[0]) {
      alert("You are not authorized to perform this operation");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this secret?")) return;

    try {
      const response = await deleteSecret(secretName, platformName, headers);
      if (response.status === 200) {
        alert("Secret deleted successfully");
        navigate(`/secrets?selectedCluster=${selectedCluster}`);
      } else {
        alert("Error deleting secret");
      }
    } catch (error) {
      console.error("Error deleting secret:", error);
      alert("Error deleting secret");
    }
  }, [userRole, secretName, platformName, headers, deleteSecret, navigate, selectedCluster]);

  const handleEditClick = useCallback(() => {
    navigate(
      `/update-secret/${secretName}?platformName=${platformName}&selectedCluster=${selectedCluster}&vaultName=${vaultId}`
    );
  }, [navigate, secretName, platformName, selectedCluster, vaultId]);

  const handleRollback = useCallback(async () => {
    setOpenWarning(false);
    setRollbackMessage("");
    
    try {
      const response = await rollbackSecret(secretName, platformName, headers);
      setRollbackMessage(
        response.data.message || "Successfully rolled back to the previous version."
      );
      setOpenSuccess(true);
    } catch (error) {
      console.error(error);
      setRollbackMessage("Failed to rollback the secret.");
      setOpenError(true);
    }
  }, [secretName, platformName, headers, rollbackSecret]);

  const handleSuccessAlertClose = useCallback(() => {
    setOpenSuccess(false);
    setVersion("current");
    navigate(
      `/view-secret/${secretName}?platformName=${platformName}&selectedCluster=${selectedCluster}&vaultName=${vaultId}`
    );
  }, [navigate, secretName, platformName, selectedCluster, vaultId]);

  const handleDialogClose = useCallback(() => {
    setOpenError(false);
    setOpenSuccess(false);
    setOpenWarning(false);
    if (version === "previous") {
      setVersion("current");
    }
  }, [version]);

  return (
    <div>
      <div style={{ paddingLeft: "2rem", marginBottom: "1rem" }}>
        <Breadcrumbs />
      </div>

      <div style={containerStyle}>
        {loading && (
          <Loading open={loading} type="skeleton" variant="code" rows={32} itemHeight={800} codebgcolor="grey.200" codecolor="grey.100" />
        )}

        {secret && !loading && (
          <Typography
            variant="h4"
            gutterBottom
            style={{ marginTop: 20, color: "#1976D2", marginBottom: 20 }}
          >
            {secretName}
          </Typography>
        )}

        {secret && !loading && (
          <SecretDetailsCard
            name={getTagValue(secretTag, "UpdatedBy")}
            avatarInitials={nameParts?.map((name) => name.charAt(0)).join("") || ""}
            timeAgo={<TimeFormatter timestamp={updatedOn} lowercase={true} />}
            clusterName={selectedCluster}
            secretName={secretName}
            env={getTagValue(secretTag, "Environment")}
            createdBy={getTagValue(secretTag, "CreatedBy")}
            project={getTagValue(secretTag, "Project")}
            store={vaultId}
          />
        )}

        <Box sx={{ width: "100%" }}>
          <Dialogbox
            open={openSuccess}
            onClose={handleSuccessAlertClose}
            title="Success"
            severity="success"
            message={
              <div>
                {secretName} rollback successfully! <br />
                Message: {rollbackMessage}
              </div>
            }
          />

          <Dialogbox
            open={openWarning}
            onClose={handleDialogClose}
            title="Warning"
            severity="warning"
            message={
              <div>
                Do you want to perform this Action? <br />
                Click on the button to continue or cancel
              </div>
            }
            onConfirm={handleRollback}
            confirmText="Confirm Rollback"
            closeText="Cancel"
          />

          <Dialogbox
            open={openError}
            onClose={handleDialogClose}
            title="Error"
            severity="error"
            message={
              <div>
                Message: {rollbackMessage || errorMessage}
              </div>
            }
          />
        </Box>

        {secret && !loading && (
          <Box
            sx={{
              ...boxStyles,
              borderRadius: "1%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box sx={{ ...headerStyles, width: "40rem" }}>
              <FormControl sx={{ marginLeft: 5, minWidth: 120 }}>
                <InputLabel>Version</InputLabel>
                <Select
                  sx={{
                    height: "36px",
                    fontSize: "0.875rem",
                    borderBottom: "2px solid #1976D2",
                  }}
                  value={version}
                  label="Version"
                  onChange={(e) => setVersion(e.target.value)}
                >
                  <MenuItem value="current">Current</MenuItem>
                  <MenuItem value="previous">Previous</MenuItem>
                </Select>
              </FormControl>

              <Stack direction="row" spacing={2} sx={{ marginRight: 5 }}>
                <Tooltip title="Delete" arrow>
                  <IconButton onClick={handleDeleteSecret} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit" arrow>
                  <IconButton onClick={handleEditClick} color="primary">
                    <Edit />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            {version === "previous" && (
              <Button
                variant="contained"
                onClick={() => setOpenWarning(true)}
                disabled={loading}
                sx={{ my: 2 }}
              >
                {loading ? "Rolling Back..." : "Rollback to Previous Version"}
              </Button>
            )}

            <Box
              sx={{
                overflow: "auto",
                my: 2,
                p: 1,
                border: "1px solid grey",
                borderRadius: 2,
                width: "55rem",
                height: "40rem",
                backgroundColor: "#f0f0f0",
              }}
            >
              <CodeEditor
                language="json"
                value={secret}
                selectedTheme="iplastic"
                readOnly="true"
              />
            </Box>
          </Box>
        )}
      </div>
    </div>
  );
}

export default ViewSecret;